if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      cartUpdateUnsubscriber = undefined;
      ogInstance = null;

      constructor() {
        super();

        this.initOrdergroove();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (variant?.id) {
            const newVariantId = variant.id;
            // Retrieve the metafield value from our pre-loaded data object
            const metafieldValue = window.variantMetafields[newVariantId].supplement_label;
            const displayElement = document.getElementById('variant-metafield-display');

            // Update the element's content with the new value
            if (displayElement && metafieldValue) {
              displayElement.innerHTML = metafieldValue;
            } else if (displayElement) {
              // Clear the element if the variant has no value for this metafield
              displayElement.innerHTML = '';
            }
          }

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            if (!variant) {
              this.setUnavailable();
              return;
            }
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          const newVariantId = variant.id;

          const allTriggers = document.getElementsByClassName('supplement-facts-wrap');
          for (const trigger of allTriggers) {
            const variantTrigger = trigger.getAttribute('data-variant');

            if (variantTrigger == newVariantId) {
              trigger.style.display = 'block';
            } else {
              trigger.style.display = 'none';
            }
          }

          this.updateMedia(variant);
          // subscription price change
          const updateOrdergroove = () => {
            const sourceog = html.getElementById(`Ordergroove-${this.sectionId}`);
            const destination = this.querySelector(
              `#Ordergroove-${this.dataset.section}`
            );

            if (sourceog && destination) {
              destination.innerHTML = sourceog.innerHTML;
            }
            const ogContainer = destination.querySelector('[data-ordergroove-enroll]');
            if (ogContainer) {
              this.ogInstance = new OgCustom(ogContainer);
            }
          };

          updateOrdergroove();

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku');
          updateSourceFromDestination('Inventory');
          updateSourceFromDestination('Badges');
          updateSourceFromDestination('PricePerItem');
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          updateSourceFromDestination('QuantityRules');
          updateSourceFromDestination('VolumeNote');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);
          if (stickyAtcBar) {
            stickyAtcBar.classList.remove('hidden');
          }

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: variant,
              },
            })
          );
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(
          `[id^="MediaGallery-${this.dataset.section}"]`
        );
        if (!productMedia) return;

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true;
          }

          return false;
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = this.decoded(text);
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = this.decoded(FoxTheme.variantStrings.addToCart);
        }

        if (!modifyClass) return;
      }

      decoded(text) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const decoded = tempDiv.textContent;
        return decoded;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);
        const volumePricing = document.getElementById(`Volume-${this.dataset.section}`);
        const stickyAtcBar = document.getElementById(`shopify-section-sticky-atc-bar`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
        if (volumePricing) volumePricing.classList.add('hidden');
        if (stickyAtcBar) stickyAtcBar.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }

      initOrdergroove() {
        const parentEl = this;
        document.addEventListener("DOMContentLoaded", function () {
          this.ogInstance = new OgCustom(parentEl);
        });
      }
    }
  );
}

class OgCustom {
  constructor(container = document) {
    this.element = container;

    this.renderCustomOg();
    this.initUpsell();
  }

  renderCustomOg = () => {
    const wrapper = this.element;
    const planInputScope = wrapper.closest('product-info') ?? wrapper;
    this.optIn = wrapper.querySelectorAll("[data-og-optin]");
    this.optOut = wrapper.querySelectorAll("[data-og-optout]");

    const quantityEl = planInputScope.querySelector('quantity-input');
    let quantityInput = planInputScope.querySelector('quantity-input input[name="quantity"]');
    this.ogRadio = wrapper.querySelectorAll("[data-og-radio]");
    this.ogRadioIsSelected = wrapper.querySelector("[data-og-radio]:checked");
    const atcEle = planInputScope.querySelector(`form button[name="add"]`);

    if(!quantityInput){
      const prodForm = planInputScope.querySelector('.product__info-container form.main-product-form');
      if(prodForm){
        if(!prodForm.querySelector('input[name="quantity"]')){
          // If it doesn't exist, create a new hidden input element and append to form
          const quantityInputEle = wrapper.createElement('input');
          quantityInputEle.setAttribute('type', 'hidden');
          quantityInputEle.setAttribute('name', 'quantity');
          quantityInputEle.setAttribute('value', '1');
          prodForm.appendChild(quantityInputEle);
          quantityInput = quantityInputEle;
        }
      }
    }

    //if subscription option is checked by default
    //hide qty selector on page load and set qty and selling plan id
    if(this.ogRadioIsSelected){
      if (quantityEl) {
        quantityEl.classList.add('hidden')
      }
      if (quantityInput) {
        quantityInput.value = Number(this.ogRadioIsSelected.getAttribute('data-ogqty') || 1);
      }
      const plan_input = planInputScope.querySelector("input[data-custom-selling]");
      if(plan_input){
        plan_input.value = this.ogRadioIsSelected.getAttribute('value');
      }
    }

    // When OTP is selected, show quantity & set to 1
    if (this.optOut.length) {
      for (let optOut of this.optOut) {
        optOut.addEventListener("click", (event) => {
          if (quantityEl) {
            quantityEl.classList.remove('hidden');
          }
          if (quantityInput) {
            quantityInput.value = 1;
          }
          const attToNextOrderBtn = event.target.closest(`.product__info-container`).querySelector(`[data-add-to-next-order]`);
          if (attToNextOrderBtn) {
            attToNextOrderBtn.classList.add('hidden');
            if(atcEle){
              const atcText = atcEle.dataset.originalAtcText;
              atcEle.querySelector(`span`).textContent = atcText;
            }
          }
          const plan_input = planInputScope.querySelector("input[data-custom-selling]");
          if(plan_input){
            plan_input.value = "";
          }
        });
      }
    }

    if (this.ogRadio.length) {
      for (let radio of this.ogRadio) {
        radio.addEventListener('change', function(event) {
          if (quantityEl) {
            quantityEl.classList.add('hidden');
          }
          const qty = event.target.dataset.ogqty;
          const planId = event.target.value;

          const plan_input = planInputScope.querySelector("input[data-custom-selling]");
          if(plan_input){
            plan_input.value = `${planId}`;
          }
          if (quantityInput) {
            quantityInput.value = qty;
          }
          const attToNextOrderBtn = event.target.closest(`.product__info-container`).querySelector(`[data-add-to-next-order]`);
          if (attToNextOrderBtn) {
            attToNextOrderBtn.classList.remove('hidden');
            if(atcEle){
              atcEle.querySelector(`span`).textContent = `No, just add it to my cart`;
            }
          }
        });
      }
    }
  };

  initUpsell = async () => {
    const wrapper = this.element;
    const subscribeEl = wrapper.querySelector("[data-ordergroove-enroll]");
    this.selectedVariant = subscribeEl?.dataset.productVar;

    const isAvailable = subscribeEl?.dataset.productAvail == "true";

    // Is product eligible for IU?
    const APP_URL = window.bmdCustom?.appUrl;

    if (!APP_URL) return;
    if (!this.selectedVariant) return;

    const requestUrl = `${APP_URL}/api/subscription/iu?product=${this.selectedVariant}`;

    const requestOptions = {
      method: "GET",
      headers: {
        'Content-Type': 'application/json'
      }
    };

    try {
      const response = await fetch(requestUrl, requestOptions);

      if (response.ok) {
        const data = await response.json();
        console.log(`IU Response:`, data);

        const canUpsell = (data?.is_eligible && isAvailable) || false;

        if (canUpsell) {
          this.getUpcomingSubscriptionOrders();
        }
      } else {
        const errorText = await response.text();
        console.error(`IU fetch failed with status ${response.status}:`, errorText);
      }
    } catch (error) {
      console.error(`An error occurred during IU eligibility fetch:`, error);
    }
  }

  getUpcomingSubscriptionOrders = async () => {
    const APP_URL = window.bmdCustom?.appUrl;
    const wrapper = this.element;

    if (!APP_URL) return

    const ogWidgetEle = wrapper.querySelector(`[data-ordergroove-enroll]`);
    const atcEle = wrapper.querySelector(`form button[name="add"]`);

    if(ogWidgetEle && APP_URL){
      const customerId = window.bmdCustom?.customer?.id;
      const customerEmail = window.bmdCustom?.customer?.email;

      if (!customerId || !customerEmail) return;

      const getSubscriptionOrdersURL = `${APP_URL}/api/subscription/upcoming`;

      const requestOptions = {
        method: "POST",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customerId,
          email: customerEmail
        })
      };

      try {
        const response = await fetch(getSubscriptionOrdersURL, requestOptions);

        if (response.ok) {
          const data = await response.json();
          console.log(`Order fetch success:`, data);

          const ordersList = data.orders?.results?.length;

          if (ordersList) {
            // ensure list is sorted by date in ascending order (oldest first)
            data.orders.results.sort((a, b) => new Date(a.place) - new Date(b.place));

            const firstItem = data.orders.results[0];
            const nextShipmentDate = firstItem?.place;

            if (nextShipmentDate) {
              const date = new Date(nextShipmentDate);
              const options = {
                year: 'numeric', // e.g. 2025
                month: 'long', // Full month name (e.g., "November")
                day: 'numeric'  // Day of the month (e.g., "19")
              };

              const upcomingOrderDate = date.toLocaleDateString('en-US', options);
              createUpcomingOrderNoteEle(ogWidgetEle,upcomingOrderDate);
              createUpcomingOrderButtonEle(atcEle);
              const upcomingOrderModal = createUpcomingOrderModal(upcomingOrderDate,null);
            }
          }
        } else {
          const errorText = await response.text();
          console.error(`Order fetch failed with status ${response.status}:`, errorText);
        }
      } catch (error) {
        console.error(`An error occurred during order fetch:`, error);
      }
    }
  }
}

function createUpcomingOrderNoteEle(targetElement,date) {
  //First remove `ordergroove-note` element if have
  if(targetElement.parentElement.querySelector(`.ordergroove-note`)){
    targetElement.parentElement.querySelector(`.ordergroove-note`).remove();
  }
  const ordergrooveNoteDiv = document.createElement('div');
  ordergrooveNoteDiv.classList.add('ordergroove-note');

  const title = document.createElement('h6');
  title.classList.add('ordergroove-note-title');
  title.textContent = `Add this to your next subscription on ${date}!`;

  const paragraph = document.createElement('p');
  paragraph.textContent = `As a subscriber, you can save time by simply adding items to your next order!`;

  ordergrooveNoteDiv.appendChild(title);
  ordergrooveNoteDiv.appendChild(paragraph);

  targetElement.parentNode.insertBefore(ordergrooveNoteDiv, targetElement);
}

function createUpcomingOrderButtonEle(targetBtnEle) {
  if(!targetBtnEle) return;
  //First remove `add-to-next-order` button element if have
  if(targetBtnEle.parentElement.querySelector(`[data-add-to-next-order]`)){
    targetBtnEle.parentElement.querySelector(`[data-add-to-next-order]`).remove();
  }
  const addToNextOrderBtn = document.createElement('button');
  addToNextOrderBtn.classList.add('product-form__submit','btn','btn--secondary', 'w-full','og-add-to-next-order');
  addToNextOrderBtn.setAttribute(`data-add-to-next-order`,``);
  addToNextOrderBtn.innerHTML = `<span>Add to my next order</span><div class="loading__spinner hidden"><svg aria-hidden="true" focusable="false" class="spinner icon icon--extra-medium" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" role="presentation"><circle class="path" fill="none" stroke-width="4" cx="33" cy="33" r="30"></circle></svg></div>`;

  targetBtnEle.parentNode.insertBefore(addToNextOrderBtn, targetBtnEle);

  const atcEle = targetBtnEle.querySelector(`span`);
  if(atcEle){
    atcEle.textContent = `No, just add it to my cart`;
  }

  //Click Event Listener
  addToNextOrderBtn.addEventListener('click', function(e) {
    e.preventDefault();
    const targetEle = e.currentTarget;
    const closestEle = targetEle.closest(`.product__info-container`);
    if(!closestEle) return;

    const customerId = window.bmdCustom?.customer?.id;
    const customerEmail = window.bmdCustom?.customer?.email;
    if (!customerId || !customerEmail) return;

    targetEle.classList.add(`btn--loading`);
    targetEle.querySelector(`.loading__spinner`).classList.remove(`hidden`);
    targetEle.setAttribute(`disabled`,``);

    const options = {
      customerId: customerId,
      email: customerEmail,
      every: closestEle.querySelector(`input[name^="selling_plan_"]:checked`).dataset.ogqty,
      quantity: +closestEle.querySelector(`input[name^="selling_plan_"]:checked`).dataset.ogqty,
      every_period: 3,
      product: closestEle.querySelector(`form input[name="id"]`).value,
      offer: window.bmdCustom?.ogOffer ?? "02d45f63ad9144ce83b8b6cb78e38383"
    }
    addProductToUpcomingSubscription(options,targetEle);
  });
}

function createUpcomingOrderModal(subscriptionDate,frequency) {
  const basicModal = document.createElement('basic-modal');
  basicModal.id = 'PopupModal_Add_next_order';
  basicModal.classList.add('drawer', 'modal', 'supplement-info-popup');
  basicModal.style.setProperty('--modal-width', '45rem');
  basicModal.setAttribute(`hidden`,``);
  basicModal.setAttribute(`inert`,``);

  const fixedOverlay = document.createElement('div');
  fixedOverlay.classList.add('fixed-overlay');
  fixedOverlay.setAttribute('aria-controls', 'PopupModal_Add_next_order');

  const drawerInner = document.createElement('div');
  drawerInner.classList.add('drawer__inner');

  const closeButton = document.createElement('button');
  closeButton.classList.add('drawer__close-btn');
  closeButton.setAttribute('aria-controls', 'PopupModal_Add_next_order');
  closeButton.setAttribute('aria-label', 'Close');
  closeButton.setAttribute('aria-expanded', 'true');
  closeButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close icon--medium" fill="none" viewBox="0 0 24 24" role="presentation"><path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;

  const drawerBody = document.createElement('div');
  drawerBody.classList.add('drawer__body', 'v-scrollable', 'h-full');

  const h4Title = document.createElement('h4');
  h4Title.classList.add('h4');
  h4Title.textContent = 'Item successfully added';

  const modalContent = document.createElement('div');
  modalContent.classList.add('modal-content');

  const p1 = document.createElement('p');
  p1.innerHTML = `Great choice! This product has been added as a subscription delivered every <span data-frequency>${frequency? `${frequency} months`: ``}</span>`;

  const p2 = document.createElement('p');
  p2.innerHTML = `Subscription starting from: <br>`;
  const dateSpan = document.createElement('span');
  dateSpan.setAttribute("style", "color: rgb(var(--color-button-hover-background)); font-weight: 600;");

  dateSpan.textContent = subscriptionDate;
  p2.appendChild(dateSpan);

  const viewOrderLink = document.createElement('a');
  viewOrderLink.href = '/apps/subscriptions/manage';
  viewOrderLink.classList.add('btn', 'w-full', 'btn--primary');
  viewOrderLink.textContent = 'View Upcoming Order';

  modalContent.appendChild(p1);
  modalContent.appendChild(p2);

  drawerBody.appendChild(h4Title);
  drawerBody.appendChild(modalContent);
  drawerBody.appendChild(viewOrderLink);

  drawerInner.appendChild(closeButton);
  drawerInner.appendChild(drawerBody);

  basicModal.appendChild(fixedOverlay);
  basicModal.appendChild(drawerInner);

  document.body.appendChild(basicModal);

  return basicModal;
}

async function addProductToUpcomingSubscription(data,btn){
  const APP_URL = window.bmdCustom?.appUrl;
  const upcomingOrderPopupModal = document.querySelector(`#PopupModal_Add_next_order`);

  if(Object.keys(data).length && APP_URL){

    const postSubscriptionOrdersURL = `${APP_URL}/api/subscription/iu`;

    const requestOptions = {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'x-og-session': window.bmdCustom?.ogSession
      },
      body: JSON.stringify(data)
    };

    try {
      const response = await fetch(postSubscriptionOrdersURL, requestOptions);

      if (response.ok) {
        const resData = await response.json();
        console.log(`Product added to next subscription success:`, resData);
        btn.classList.remove(`btn--loading`);
        btn.querySelector(`.loading__spinner`).classList.add(`hidden`);
        btn.removeAttribute(`disabled`);
        if(upcomingOrderPopupModal){
          upcomingOrderPopupModal.querySelector(`[data-frequency]`).textContent = `${data.every} months`;
          upcomingOrderPopupModal.removeAttribute(`hidden`);
          upcomingOrderPopupModal.removeAttribute(`inert`);
          upcomingOrderPopupModal.setAttribute(`open`,``);
          upcomingOrderPopupModal.setAttribute(`active`,``);
        }
      } else {
        const errorText = await response.text();
        console.error(`Order added failed with status ${response.status}:`, errorText);
        btn.classList.remove(`btn--loading`);
        btn.querySelector(`.loading__spinner`).classList.add(`hidden`);
        btn.removeAttribute(`disabled`);
        if(upcomingOrderPopupModal){
          upcomingOrderPopupModal.setAttribute(`hidden`,``);
          upcomingOrderPopupModal.setAttribute(`inert`,``);
          upcomingOrderPopupModal.removeAttribute(`open`);
          upcomingOrderPopupModal.removeAttribute(`active`);
        }
      }
    } catch (error) {
      console.error(`An error occurred during order add:`, error);
      btn.classList.remove(`btn--loading`);
      btn.querySelector(`.loading__spinner`).classList.add(`hidden`);
      btn.removeAttribute(`disabled`);
      if(upcomingOrderPopupModal){
        upcomingOrderPopupModal.setAttribute(`hidden`,``);
        upcomingOrderPopupModal.setAttribute(`inert`,``);
        upcomingOrderPopupModal.removeAttribute(`open`);
        upcomingOrderPopupModal.removeAttribute(`active`);
      }
    }
  }
}
